import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useAuth } from "../../contexts/AuthContext";
import { HiChevronLeft } from "react-icons/hi";
import { ActivityMeasurement, Session } from "../../types/post";
import MasterSessionDetails from "./MasterSessionDetails";
import AILogButton, { type AILogFunction } from "../AILogButton";

// Modal for adding session details to training logs

interface AddSessionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSessionSaved: (
    sessionId: string | null,
    measurements: ActivityMeasurement[],
  ) => void;
  trainingLogDate: string;
  existingSession?: Session | null;
  /** Override the overlay z-index when stacking above another modal (e.g. training log form). */
  overlayZIndexClass?: string;
  /** Mirror hidden-column selections onto the returned measurements' `variable.hidden` flags. */
  syncHiddenColumns?: boolean;
}

const AddSessionDetailsModal: React.FC<AddSessionDetailsModalProps> = ({
  isOpen,
  onClose,
  onSessionSaved,
  trainingLogDate,
  existingSession,
  overlayZIndexClass = "z-[9999]",
  syncHiddenColumns = false,
}) => {
  const { currentUser } = useAuth();

  const [plannedMeasurements, setPlannedMeasurements] = useState<
    ActivityMeasurement[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [aiStatus, setAiStatus] = useState<"idle" | "connecting" | "listening">(
    "idle",
  );
  const [aiPrompt, setAiPrompt] = useState<string>("");
  const [aiLogFunctions, setAiLogFunctions] = useState<AILogFunction[]>([]);
  // Guard against re-initializing during open
  const initializedRef = useRef(false);

  const handleAIConfigReady = useCallback(
    (prompt: string, logFunctions: AILogFunction[]) => {
      setAiPrompt(prompt);
      setAiLogFunctions(logFunctions);
    },
    [],
  );

  // Keep a stable reference so MasterSessionDetails doesn't re-initialize on every render.
  const normalizedExistingSession = useMemo(() => {
    if (!existingSession) return undefined;
    if (existingSession.id) {
      return existingSession as Session & { id: string };
    }
    return { ...existingSession, id: "" };
  }, [existingSession]);

  // Initialize when modal opens - clear or load from existing session
  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      if (existingSession) {
        setPlannedMeasurements(existingSession.ActivityMeasurementList || []);
      } else {
        setPlannedMeasurements([]);
      }
      initializedRef.current = true;
    }
    if (!isOpen) {
      initializedRef.current = false;
    }
  }, [isOpen, existingSession]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPlannedMeasurements([]);
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 ${overlayZIndexClass} bg-black bg-opacity-75 backdrop-blur-sm overflow-hidden`}
    >
      <div className="flex flex-col h-full md:items-center md:justify-center md:overflow-auto">
        <div className="w-full max-w-2xl mx-auto bg-base-100 md:rounded-xl shadow-lg flex flex-col h-full md:h-auto md:max-h-[70vh] overflow-hidden">
          {/* Header - Fixed on mobile */}
          <div className="flex justify-between items-center p-4 pb-6 md:p-6 md:pb-8 flex-shrink-0 bg-base-100 md:rounded-t-xl">
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-base-300 bg-base-300 transition-colors"
                style={{ touchAction: "manipulation" }}
              >
                <HiChevronLeft className="h-8 w-8 text-base-content/70" />
              </button>
              <div>
                <h2 className="text-xl text-base-content font-grotesk">
                  {existingSession ? "Edit" : "Add"}{" "}
                  <strong>Session Details</strong>
                </h2>
              </div>
            </div>
            <AILogButton
              prompt={aiPrompt}
              logFunctions={aiLogFunctions}
              title="Dictate session with AI"
              onStatusChange={(s) =>
                setAiStatus(
                  s === "listening"
                    ? "listening"
                    : s === "connecting"
                      ? "connecting"
                      : "idle",
                )
              }
            />
          </div>

          {/* Content - Scrollable */}
          <div
            className="flex-1 overflow-y-auto overflow-x-visible min-h-0"
            style={{
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              touchAction: "pan-y",
            }}
          >
            <div className="px-0 bg-base-100">
              {/* Master Session Details Component */}
              <MasterSessionDetails
                plannedMeasurements={plannedMeasurements}
                onPlannedMeasurementsChange={setPlannedMeasurements}
                loading={false}
                error={error}
                onErrorChange={setError}
                existingSession={normalizedExistingSession}
                floatingActivityButton={false}
                hideFloatingAIButton
                onAIConfigReady={handleAIConfigReady}
                syncHiddenColumnsToVariables={syncHiddenColumns}
              />
            </div>
          </div>

          {/* Footer */}
          <div
            className="p-8 pb-12 flex-shrink-0 relative z-50 flex items-center gap-3"
            style={{
              pointerEvents: "auto",
              touchAction: "manipulation",
            }}
          >
            {aiStatus !== "idle" ? (
              <span className="flex-1 text-left text-base-content/80 font-grotesk text-sm">
                {aiStatus === "connecting" ? (
                  <>
                    <span className="loading loading-spinner loading-xs mr-1"></span>
                    Loading...
                  </>
                ) : (
                  <span className="flex items-center justify-between gap-2">
                    Listening...
                    <span className="inline-flex items-center gap-[3px] h-4">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <span
                          key={i}
                          className="w-[3px] rounded-full bg-red-500"
                          style={{
                            animation: `listeningBar 1.2s ease-in-out ${i * 0.15}s infinite`,
                            height: "4px",
                          }}
                        />
                      ))}
                    </span>
                    <style>{`
                      @keyframes listeningBar {
                        0%, 100% { height: 4px; }
                        50% { height: 16px; }
                      }
                    `}</style>
                  </span>
                )}
                <div className="text-xs text-base-content/50">
                  {aiStatus === "connecting"
                    ? "Setting up microphone."
                    : "Speak to log."}
                </div>
              </span>
            ) : (
              <button
                onClick={() => {
                  // iOS fix: Ensure any focused input is blurred before submit
                  if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                  }
                  if (plannedMeasurements.length === 0) {
                    onClose();
                    return;
                  }
                  // Pass measurements back to parent — no Firestore write here.
                  // The session will be created/updated when the training log is submitted.
                  onSessionSaved(
                    existingSession?.id ?? null,
                    plannedMeasurements,
                  );
                  onClose();
                }}
                disabled={plannedMeasurements.length === 0}
                className="bg-primary text-white py-4 px-6 font-grotesk font-bold flex-1 rounded-xl disabled:bg-primary/30 disabled:text-white/50 disabled:cursor-not-allowed"
                style={{ touchAction: "manipulation" }}
              >
                {existingSession ? "Update Session" : "Save Session"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSessionDetailsModal;
